export const TEMPLATE_TABS = {
  ALL: 'all',
  PURCHASE_HISTORY: 'purchase_history',
};

export const TEMPLATE_TAB_OPTIONS = [
  {
    id: TEMPLATE_TABS.ALL,
    name: 'All Template',
  },
  {
    id: TEMPLATE_TABS.PURCHASE_HISTORY,
    name: 'Purchase History',
  },
];

export const TEMPLATE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

export const TEMPLATE_STATUS_OPTIONS = [
  {
    id: TEMPLATE_STATUS.ACTIVE,
    name: 'Active',
  },
  {
    id: TEMPLATE_STATUS.INACTIVE,
    name: 'Inactive',
  },
];

export const IMPORT_TYPE_DEFAULT = {
  type: '',
  link: '',
  duration: 0,
  file: null,
  valid: false,
  library: null,
};

export const TEMPLATE_TYPE = {
  BANNER: 'Banner',
  VIDEO: 'Video',
  CTV: 'CTV',
};

import {
  bytesToKB,
  bytesToMB,
  calculateGCD,
  LIMIT_IMAGE_SIZE_KB,
  LIMIT_VIDEO_SIZE_MB,
} from 'utilities/file';

export const sizes = [
  { title: '300x250', subtitle: 'Medium Rectangle' },
  { title: '300x600', subtitle: 'Large Skyscraper' },
  { title: '160x600', subtitle: 'Wide Skyscraper' },
  { title: '728x90', subtitle: 'Leaderboard' },
  { title: '320x50', subtitle: 'Mobile Leaderboard' },
  { title: '970x250', subtitle: 'Billboard' },
];

export const VIDEO_RATIO_ACCEPT = '16:9';

export const videoProgrammaticAdLengths = [
  {
    videoLength: 6,
    title: ':06-sec',
  },
  {
    videoLength: 15,
    title: ':15-sec',
  },
  {
    videoLength: 30,
    title: ':30-sec',
  },
  {
    videoLength: 60,
    title: ':60-sec',
  },
];

export const videoCTVAdLengths = [
  {
    videoLength: 15,
    title: ':15-sec',
  },
  {
    videoLength: 30,
    title: ':30-sec',
  },
];

export const BANNER_OPTIONS_FILTER = sizes.map((el) => ({
  id: el.title,
  name: el.title,
}));

export const VIDEO_OPTIONS_FILTER = videoProgrammaticAdLengths.map((el) => ({
  id: `${el.videoLength}-sec`,
  name: el.title.replace(':', ''),
}));

export const CREATIVE_TYPE = {
  BANNER: 'Banner',
  VIDEO: 'Video',
  CTV: 'CTV',
};

export const IMPORT_TYPE = {
  LINK: 'FromUrl',
  VIDEO: 'FromFile',
  LIBRARY: 'FromLibrary',
  FULLSET: 'FullSet',
};

export const fileNotMeetSpec = `File does not meet the required specifications. Please check the 'Template Specification Guidelines' and re-upload.`;

export const isValidVideoDuration = (duration) => {
  return videoProgrammaticAdLengths.some(
    (el) => duration >= el.videoLength - 1 && duration <= el.videoLength + 1
  );
};

export const isValidCTVVideoDuration = (duration) => {
  return videoCTVAdLengths.some(
    (el) => duration >= el.videoLength - 1 && duration <= el.videoLength + 1
  );
};

export const isValidVideo = (file) => {
  return (
    bytesToMB(file.size) <= LIMIT_VIDEO_SIZE_MB &&
    isValidVideoDuration(file.duration) &&
    file.ratio === VIDEO_RATIO_ACCEPT
  );
};

export const isValidImage = (file) => {
  return (
    bytesToKB(file.size) <= LIMIT_IMAGE_SIZE_KB &&
    sizes.some((el) => el.title === `${file.width}x${file.height}`)
  );
};

export const isValidProgrammaticVideo = (file, type) => {
  let errors = [];
  const genaralInfor = file?.media?.track.find((d) => d['@type'] === 'General');
  const videoInfor = file?.media?.track.find((d) => d['@type'] === 'Video');
  const audioInfor = file?.media?.track.find((d) => d['@type'] === 'Audio');

  /* All videos should be provided in .mp4 format. */
  if (type !== 'video/mp4') {
    errors.push('The video must be provided in .mp4 format.');
  }

  /* All videos must be under the 100MB max file size per creative unit. */
  if (bytesToMB(genaralInfor.FileSize) > LIMIT_VIDEO_SIZE_MB) {
    errors.push(
      'The video must be under the 100MB max file size per creative unit.'
    );
  }

  /* All videos must be in 16:9 aspect ratio (no square videos or vertical videos). */
  if (videoInfor?.Width && videoInfor?.Height) {
    const ratio = calculateGCD(
      Number(videoInfor?.Width),
      Number(videoInfor?.Height)
    );
    const aspectRatio = `${videoInfor?.Width / ratio}:${
      videoInfor?.Height / ratio
    }`;
    if (aspectRatio !== VIDEO_RATIO_ACCEPT) {
      errors.push('The video must be in 16:9 aspect ratio.');
    }
  } else errors.push('The video must be in 16:9 aspect ratio.');

  /* All Programmatic Video (Pre–roll / Mid-roll) videos must adhere exactly to one of four standard ad lengths: 
    06-sec || 15-sec || 30-sec || 60-sec */
  if (!videoInfor?.Duration || !isValidVideoDuration(videoInfor?.Duration)) {
    errors.push(
      'The video must adhere exactly to one of four standard ad lengths: 06-sec / 15-sec / 30-sec / 60-sec.'
    );
  }

  return errors;
};

export const isValidCTVVideo = (file, type) => {
  let errors = [];
  const genaralInfor = file?.media?.track.find((d) => d['@type'] === 'General');
  const videoInfor = file?.media?.track.find((d) => d['@type'] === 'Video');
  const audioInfor = file?.media?.track.find((d) => d['@type'] === 'Audio');
  /* All CTV/OTT videos must also meet the following technical requirements: */
  // Dimensions (px): 1280x720 (minimum) // 1920x1080 (maximum)
  if (
    !(
      videoInfor?.Width >= 1260 &&
      videoInfor?.Width <= 1920 &&
      videoInfor?.Height >= 720 &&
      videoInfor?.Height <= 1080
    )
  ) {
    errors.push(
      'The video must be with dimensions between 1280x720 and 1920x1080'
    );
  }
  // Bit Rate: 10-40 mbps
  const bitRateMbps = Number(videoInfor?.BitRate) / 1000000; // convert bps -> mpds
  if (bitRateMbps < 10 || bitRateMbps > 40) {
    errors.push('The video must be with bit rate from 10 to 40 mbps.');
  }
  // Bit Depth: 8 or 16
  const bitDepth = Number(videoInfor.BitDepth);
  if (![8, 16].includes(bitDepth)) {
    errors.push('The video must be with bit depth is 8 or 16.');
  }
  // Codec: H.264 or Apple ProRes 422 HQ
  if (!['avc1', 'apcn'].includes(videoInfor.CodecID)) {
    errors.push(
      'The video must be with codec is H.264 or Apple ProRes 422 HQ.'
    );
  }
  // Frame Rate: 23.98 or 25 or 29.97
  const frameRate = parseFloat(videoInfor.FrameRate).toFixed(2);
  if (!['23.98', '25.00', '29.97'].includes(frameRate.toString())) {
    errors.push('The video must be with frame rate is 23.98 or 25 or 29.97.');
  }

  /* Audio Specs: */
  // 2-Channel Stereo
  if (audioInfor?.Channels !== '2') {
    errors.push('The audio of this video must have 2-Channel Stereo.');
  }

  // PCM or AAC
  if (!['PCM', 'AAC'].includes(audioInfor?.Format)) {
    errors.push('The audio of this video must be with format is PCM or AAC.');
  }

  // 16 or 24 Bit Depth, format AAC is lossy audio => skip check bit depth
  const bitDepthAudio = Number(audioInfor?.BitDepth);
  if (audioInfor?.Format === 'PCM' && ![16, 24].includes(bitDepthAudio)) {
    errors.push('The audio of this video must be with bit depth is 16 or 24.');
  }

  // 192-256 kbps Bit Rate
  const bitRateAudioKbps = Number(audioInfor?.BitRate) / 1000; // convert bps -> mpds
  if (!bitRateAudioKbps || bitRateAudioKbps < 192 || bitRateAudioKbps > 256) {
    errors.push(
      'The audio of this video must be with bit rate from 192 to 256 kbps.'
    );
  }

  /* All videos should be provided in .mp4 format. */
  if (type !== 'video/mp4') {
    errors.push('The video must be provided in .mp4 format.');
  }

  if (bytesToMB(genaralInfor.FileSize) > 250) {
    /* All CTV/OTT videos must be under the 250MB max file size per creative unit. */
    errors.push(
      'The video must be within under the 250MB max file size per creative unit.'
    );
  }

  /* All videos must be in 16:9 aspect ratio (no square videos or vertical videos). */
  const ratio = calculateGCD(
    Number(videoInfor?.Width),
    Number(videoInfor?.Height)
  );
  const aspectRatio = `${videoInfor?.Width / ratio}:${
    videoInfor?.Height / ratio
  }`;
  if (aspectRatio !== VIDEO_RATIO_ACCEPT) {
    errors.push('The video must be in 16:9 aspect ratio.');
  }

  /* All Programmatic Video (Pre–roll / Mid-roll) videos must adhere exactly to one of two standard ad lengths: 
     15-sec || 30-sec ||  */
  const duration = Math.floor(videoInfor?.Duration);
  if (!isValidCTVVideoDuration(duration)) {
    errors.push(
      'The video must adhere exactly to one of two standard ad lengths: 15-sec / 30-sec.'
    );
  }

  return errors;
};
